import { createContext, useContext, Dispatch, SetStateAction } from "react";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";

import { IngredientReference } from "src/api/client-api/ingredientReference/useAllIngredientReferences";
import { GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns.tsx";

/**
 * NOTE — This file is intentionally separated from
 * `IngredientReferenceListContext.tsx` (which exports the
 * `IngredientReferenceListContextProvider` component) to keep the module
 * compatible with Vite React Fast Refresh's "consistent components exports"
 * rule. Mixing a hook (`useIngredientReferenceListContext`) with a component
 * export in the same module breaks HMR: a stale provider stays mounted while
 * the new hook references a brand-new context instance, causing `useContext`
 * to return `undefined` and the runtime check below to throw.
 *
 * See https://github.com/vitejs/vite-plugin-react-swc#consistent-components-exports
 */
export type IngredientReferenceListContextInitialValues = {
    page: MRT_PaginationState;
    setPage: Dispatch<SetStateAction<MRT_PaginationState>>;

    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;

    totalItems: number | undefined;

    ingredientReferences: IngredientReference[] | undefined;
    mrt_columns: GridColumnsType<IngredientReference>;
    isLoading: boolean;

    search: string;
    delayedSearch: string;
    setSearch: (arg1: string) => void;
    resetSearch: () => void;

    showCreateModal: boolean;
    setShowCreateModal: Dispatch<SetStateAction<boolean>>;

    gridKey: string;
};

export const IngredientReferenceListContext = createContext<IngredientReferenceListContextInitialValues | undefined>(
    undefined,
);

export const useIngredientReferenceListContext = () => {
    const context = useContext(IngredientReferenceListContext);
    if (context === undefined) {
        throw new Error(
            "useIngredientReferenceListContext must be used within an IngredientReferenceListContextProvider",
        );
    }
    return context;
};
